import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';
import './FAQ.css';

const faqs = [
  {
    question: 'How much does custom enterprise software development cost?',
    answer: 'The cost of custom software development varies based on project complexity, integrations, and platform requirements. At Wayug Infotech, we provide transparent, phased pricing starting with a comprehensive discovery phase to accurately forecast your digital investment. Simple web applications can start from ₹3–8 lakhs, while complex enterprise platforms typically range from ₹15–60 lakhs depending on scale.',
  },
  {
    question: 'What is the ROI of migrating to cloud computing?',
    answer: 'Cloud migration typically yields a 30–50% reduction in IT operational costs within the first year. It eliminates on-premise hardware maintenance, provides dynamic scalability, and significantly enhances remote workforce productivity. Most of our clients achieve full ROI within 12–18 months of completing their migration.',
  },
  {
    question: 'How do you ensure cybersecurity during app development?',
    answer: 'We employ a "Security by Design" approach. This includes automated vulnerability scanning at every CI/CD stage, mandatory peer code reviews, end-to-end encryption for all data in transit and at rest, and rigorous penetration testing before any software deployment. We are ISO 27001 certified and follow OWASP top-10 guidelines.',
  },
  {
    question: 'Do you offer post-launch support and maintenance?',
    answer: 'Yes, our managed services practice provides 24/7 SLA-driven support, continuous proactive monitoring, and regular security patching to ensure your digital products perform flawlessly long after launch. We offer flexible support tiers — from business-hours helpdesk to fully managed 24/7 NOC operations.',
  },
  {
    question: 'How long does a typical IT project take to deliver?',
    answer: 'Project timelines depend on scope. A discovery phase typically takes 2–4 weeks. A medium-complexity web platform takes 3–5 months from kickoff to launch. Cloud migrations are typically completed in 6–12 weeks. We follow agile delivery with bi-weekly sprint demos so you see progress every step of the way.',
  },
  {
    question: 'Which industries does Wayug Infotech specialise in?',
    answer: 'We have deep domain expertise across Financial Services (banking, fintech), Healthcare (HIPAA-compliant systems, telemedicine), Manufacturing (Industry 4.0, IIoT), Government (e-governance), Retail & E-commerce, and Education. Each vertical has unique compliance and performance requirements — our teams understand them all.',
  },
  {
    question: 'Can Wayug Infotech integrate AI into our existing business systems?',
    answer: 'Absolutely. We specialise in AI augmentation — integrating large language models (LLMs), machine learning, and robotic process automation (RPA) into your existing ERP, CRM, or custom software without rebuilding from scratch. Most AI integrations are live within 8–14 weeks.',
  },
  {
    question: 'What makes Wayug different from other IT companies in India?',
    answer: 'Three things: Engineering depth, delivery transparency, and a genuine partnership model. We are not a body-shop — we embed with your team, understand your business goals, and build technology that drives measurable outcomes. We have maintained a 100% client retention rate since 2019 and a 99.9% uptime SLA across all managed service contracts.',
  },
];

export default function FAQ() {
  const [openIndex, setOpenIndex] = useState(0);

  const toggleFaq = (index) => {
    setOpenIndex(openIndex === index ? -1 : index);
  };

  // Generate FAQPage JSON-LD Schema
  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((faq) => ({
      '@type': 'Question',
      name: faq.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: faq.answer,
      },
    })),
  };

  return (
    <section className="faq-section" id="faq" aria-labelledby="faq-heading">
      <script type="application/ld+json">
        {JSON.stringify(faqSchema)}
      </script>

      <div className="container faq-container">
        <div className="section-header section-header--center">
          <span className="section-label">FAQ</span>
          <h2 id="faq-heading" className="section-title">
            Frequently Asked <span className="gradient-text">Questions</span>
          </h2>
          <p className="section-subtitle">
            Everything you need to know about partnering with Wayug Infotech for your digital transformation.
          </p>
        </div>

        <div className="faq-list" role="list">
          {faqs.map((faq, index) => {
            const isOpen = openIndex === index;
            return (
              <div 
                key={index} 
                className={`faq-item ${isOpen ? 'active' : ''}`}
                onClick={() => toggleFaq(index)}
                role="listitem"
              >
                <button 
                  className="faq-question" 
                  aria-expanded={isOpen}
                  aria-controls={`faq-answer-${index}`}
                  id={`faq-question-${index}`}
                >
                  {faq.question}
                  <motion.div
                    className="faq-icon"
                    animate={{ rotate: isOpen ? 180 : 0 }}
                    transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                  >
                    <ChevronDown size={20} />
                  </motion.div>
                </button>
                <AnimatePresence>
                  {isOpen && (
                    <motion.div
                      id={`faq-answer-${index}`}
                      className="faq-answer-wrapper"
                      role="region"
                      aria-labelledby={`faq-question-${index}`}
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: 'auto', opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
                    >
                      <div className="faq-answer">
                        <p>{faq.answer}</p>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            );
          })}
        </div>

        {/* CTA at bottom of FAQ */}
        <div className="faq-cta">
          <p>Still have questions? Our experts are ready to help.</p>
          <a
            href="https://wa.me/918287999236?text=Hi!%20I%20have%20some%20questions%20about%20your%20IT%20services."
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-primary"
          >
            Ask Us on WhatsApp
          </a>
        </div>
      </div>
    </section>
  );
}
